declare global {
  interface Window {
    MSStream?: unknown;
  }
}


import { createContext, useContext, useEffect, useState } from 'react';

// Define the type for the beforeinstallprompt event
interface BeforeInstallPromptEvent extends Event {
  readonly platforms: string[];
  readonly userChoice: Promise<{
    outcome: 'accepted' | 'dismissed';
    platform: string;
  }>;
  prompt(): Promise<void>;
}

type PwaContextType = {
  installPromptEvent: BeforeInstallPromptEvent | null;
  isIos: boolean;
  canBeInstalled: boolean;
  triggerInstall: () => void;
};

const PwaContext = createContext<PwaContextType | undefined>(undefined);

// Regex for iOS detection moved to top level for performance
const IOS_REGEX = /iPad|iPhone|iPod/;

export const PwaProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [installPromptEvent, setInstallPromptEvent] = useState<BeforeInstallPromptEvent | null>(
    null
  );
  const [isIos, setIsIos] = useState(false);
  useEffect(() => {
    // Detect iOS
    const isIOS =
      IOS_REGEX.test(navigator.userAgent) &&
      !window.MSStream;
    setIsIos(isIOS);

    const handleBeforeInstallPrompt = (event: Event) => {
      event.preventDefault();
      setInstallPromptEvent(event as BeforeInstallPromptEvent);
      console.log('[PWA] beforeinstallprompt event captured.');
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, []);

  const triggerInstall = () => {
    if (installPromptEvent) {
      installPromptEvent.prompt();
      console.log('[PWA] Installation prompt triggered.');
    }
  };

  const canBeInstalled = !!installPromptEvent || isIos;

  const value = { installPromptEvent, isIos, canBeInstalled, triggerInstall };

  return <PwaContext.Provider value={value}>{children}</PwaContext.Provider>;
};

export const usePwa = (): PwaContextType => {
  const context = useContext(PwaContext);
  if (context === undefined) {
    throw new Error('usePwa must be used within a PwaProvider');
  }
  return context;
};
